import React, { Fragment, useState, useEffect } from "react";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import { parseCEL } from "react-querybuilder/parseCEL"
import ModalVirtualTagEditor from "./ModalVirtualTagEditor.jsx";
import JobStatusModal from "./JobStatusModal.jsx";
import VirtualTagsTable from "../../components/VirtualTagsTable.jsx";
import TableCard from "../../components/ui-components/TableCard";
import EmptyState from "../../components/ui-components/EmptyState";

const API_URL = process.env.REACT_APP_API_URL;

const VirtualTags = () => {
    const { keycloak } = useKeycloak();
    const queryClient = useQueryClient();
    const [ruleEditorWindow, setRuleEditorWindow] = useState(false);
    const [virtualTag, setVirtualTag] = useState();
    const [data, setData] = useState([]);
    const [groupingValues, setGroupingValues] = useState([]);
    const [notification, setNotification] = useState({ show: false, message: "", type: "" });
    const [shouldPoll, setShouldPoll] = useState(false);
    const [currentJobId, setCurrentJobId] = useState(null);
    const [showJobStatusModal, setShowJobStatusModal] = useState(false);

    const getVirtualTags = async (token) => {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("Authorization", "Bearer " + token);

        let method = "GET";

        let requestOptions = {
            method: method,
            headers: headers,
        };
        // const response = await fetch(API_URL + "/get-virtual-tags", requestOptions);
        const response = await fetch(API_URL + "/api/v1/virtual-tags", requestOptions);

        if (!response.ok) {
            throw new Error("Failed to fetch virtual tags");
        }

        return response.json();
    }

    // retrieve virtual tags
    const { isLoading: isLoadingVirtualTags, error: virtualTagsError, refetch: refetchVirtualTags } = useQuery({
        queryKey: ["virtualTags"],
        queryFn: () => getVirtualTags(keycloak.token),
        enabled: keycloak.token !== "",
        refetchOnMount: true,
        refetchOnWindowFocus: false,
        onSuccess: (data) => {
            // console.log("API response data", data);
            // Sort the virtual tags by order column
            if (data?.virtual_tags?.virtual_tags) {
                const sortedTags = [...data.virtual_tags.virtual_tags].sort((a, b) => {
                    if (a.order === null || a.order === undefined) return 1;
                    if (b.order === null || b.order === undefined) return -1;
                    return a.order - b.order;
                });
                setData(sortedTags);
            } else {
                setData([]);
            }
        },
        onError: (error) => {
            console.error("Error fetching virtual tags:", error);
            setData([]);
        }
    });

    // select the given virtual tag and open the rule editor
    const selectVirtualTag = (virtualTag) => {

        // console.log("selectVirtualTag - virtualTag", virtualTag);

        // Ensure the virtual tag has the correct structure for the modal
        const virtualTagForModal = { ...virtualTag };

        // If there's a cel_query, convert it to rules format
        if (virtualTag.rules && virtualTag.rules !== "") {
            
            // Check if rules is already an object or a JSON string
            let rules;
            if (typeof virtualTag.rules === 'string') {
                rules = JSON.parse(virtualTag.rules);
            } else {
                rules = virtualTag.rules;
            }
            
            rules.forEach(rule => rule.query = parseCEL(rule.query));
            virtualTagForModal.rules = rules;
        } else {
            // Create default rules structure
            virtualTagForModal.rules = [{
                id: Date.now() + Math.random(),
                setValue: "",
                priority: 0,
                query: {
                    combinator: 'and',
                    rules: [{ field: '', operator: '=', value: '' }]
                }
            }];
        }

        // console.log("selectVirtualTag - virtualTagForModal", virtualTagForModal);
        
        setVirtualTag(virtualTagForModal);
        setRuleEditorWindow(true);
    }

    const retrieveSearchTerms = async () => {
		const headers = new Headers();
		headers.append("Content-Type", "application/json");
		headers.append("Authorization", "Bearer " + keycloak.token);

		let method = "POST";

		const requestBody = {
			category: "Category",
		};

		let requestOptions = {
			method: method,
			headers: headers,
			body: JSON.stringify(requestBody),
		};

		const response = await fetch(
			API_URL + `/get-search-terms?useTagKeys=true&onlyGroupingValues=true`,
			requestOptions
		);

		if (!response.ok) {
			throw new Error("Failed to fetch data");
		}

		return response.json();
	};

	useQuery(
		["searchTerms"],
		() => retrieveSearchTerms(),
		{
			onSuccess: (data) => {
				if (data.SearchTerms) {
    				const tmpData = data.SearchTerms.map((value) => ({
                        label: value["term_display"],
                        name: value["technical_term"],
                    }));

                    setGroupingValues(tmpData);
				}
			},
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

    // Mutation for creating virtual tags
    const createVirtualTagMutation = useMutation({
        mutationFn: async (virtualTagData) => {
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("Authorization", "Bearer " + keycloak.token);

            const requestOptions = {
                method: "POST",
                headers: headers,
                body: JSON.stringify(virtualTagData),
            };

            // const response = await fetch(API_URL + "/create-virtual-tag", requestOptions);
            const response = await fetch(API_URL + "/api/v1/virtual-tags", requestOptions);
            
            if (!response.ok) {
                throw new Error("Failed to create virtual tag");
            }

            return response.json();
        },
        onSuccess: (data) => {
            // Invalidate and refetch virtual tags to ensure consistency
            queryClient.invalidateQueries({ queryKey: ["virtualTags"] });
            // Add the new virtual tag to the local state
            setData(prevData => [...prevData, data.virtual_tag]);
            // Close the modal after successful save
            setRuleEditorWindow(false);
        },
        onError: (error) => {
            console.error("Error creating virtual tag:", error);
        }
    });

    // Mutation for updating virtual tags
    const updateVirtualTagMutation = useMutation({
        mutationFn: async (virtualTagData) => {
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("Authorization", "Bearer " + keycloak.token);

            const requestOptions = {
                method: "PUT",
                headers: headers,
                body: JSON.stringify(virtualTagData),
            };

            const response = await fetch(API_URL + "/api/v1/virtual-tags/" + virtualTagData.id, requestOptions);
            
            if (!response.ok) {
                console.log("response", response);
                throw new Error("Failed to update virtual tag");
            }

            return response.json();
        },
        onSuccess: (data) => {
            // Invalidate and refetch virtual tags to ensure consistency
            queryClient.invalidateQueries({ queryKey: ["virtualTags"] });
            // Update the virtual tag in the local state
            setData(prevData => prevData.map(tag => tag.id === data.virtual_tag.id ? data.virtual_tag : tag));

            // Close the modal after successful save
            setRuleEditorWindow(false);
        },
        onError: (error) => {
            console.error("Error updating virtual tag:", error);
        }
    });

    // Mutation for deleting virtual tags
    const deleteVirtualTagMutation = useMutation({
        mutationFn: async (virtualTagId) => {
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("Authorization", "Bearer " + keycloak.token);

            const requestOptions = {
                method: "DELETE",
                headers: headers,
            };

            const response = await fetch(API_URL + "/api/v1/virtual-tags/" + virtualTagId, requestOptions);
            
            if (!response.ok) {
                throw new Error("Failed to delete virtual tag");
            }

            return response;
        },
        onSuccess: () => {
            // Invalidate and refetch virtual tags to ensure consistency
            queryClient.invalidateQueries({ queryKey: ["virtualTags"] });
            // Remove the virtual tag from the local state
            setData(prevData => prevData.filter(tag => tag.id !== virtualTag?.id));
            
            // Close the modal after successful deletion
            setRuleEditorWindow(false);
            
            // Show success notification
            setNotification({
                show: true,
                message: "Virtual tag deleted successfully",
                type: "success"
            });
        },
        onError: (error) => {
            console.error("Error deleting virtual tag:", error);
            setNotification({
                show: true,
                message: "Failed to delete virtual tag",
                type: "error"
            });
        }
    });

    // Mutation for updating virtual tag order
    const updateVirtualTagOrderMutation = useMutation({
        mutationFn: async (virtualTagsWithOrder) => {
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("Authorization", "Bearer " + keycloak.token);

            const requestOptions = {
                method: "PUT",
                headers: headers,
                body: JSON.stringify({ virtual_tags: virtualTagsWithOrder }),
            };

            const response = await fetch(API_URL + "/api/v1/virtual-tags/order", requestOptions);
            
            if (!response.ok) {
                throw new Error("Failed to update virtual tag order");
            }

            return response.json();
        },
        onSuccess: () => {
            // Invalidate and refetch virtual tags to ensure consistency
            queryClient.invalidateQueries({ queryKey: ["virtualTags"] });
            // Show success notification
            setNotification({
                show: true,
                message: "Virtual tag order updated successfully",
                type: "success"
            });
        },
        onError: (error) => {
            console.error("Error updating virtual tag order:", error);
            setNotification({
                show: true,
                message: "Failed to update virtual tag order",
                type: "error"
            });
        }
    });

    const saveVirtualTag = (updatedVirtualTag) => {
        const virtualTagToSave = updatedVirtualTag || virtualTag;
        
        if (!virtualTagToSave) {
            console.error("No virtual tag data to save");
            return;
        }

        // Validate required fields
        if (!virtualTagToSave.name || virtualTagToSave.name.trim() === "") {
            return;
        }

        // Prepare the data for the backend
        const virtualTagData = {
            id: virtualTagToSave.id,
            customer_id: keycloak.tokenParsed?.sub || "", // Use customer ID from token
            name: virtualTagToSave.name.trim(),
            rules: virtualTagToSave.rules, // Send rules as array, not JSON string
            active: virtualTagToSave.active,
        };

        // Determine if this is a create or update operation
        if (virtualTagToSave.id && virtualTagToSave.id > 0) {
            // Update existing virtual tag
            updateVirtualTagMutation.mutate(virtualTagData);
        } else {
            // Create new virtual tag
            createVirtualTagMutation.mutate(virtualTagData);
        }
    }

    const createVirtualTag = () => {
        setVirtualTag(null);
        setRuleEditorWindow(true);
    }

    // Function to handle drag and drop reordering
    const handleVirtualTagReorder = (reorderedData) => {
        // Update the order property for each virtual tag
        const virtualTagsWithOrder = reorderedData.map((tag, index) => ({
            ...tag,
            order: index + 1
        }));

        // Update local state immediately for responsive UI
        setData(virtualTagsWithOrder);

        // Send the updated order to the backend
        updateVirtualTagOrderMutation.mutate(virtualTagsWithOrder);
    }

    // Mutation for processing virtual tags
    const processVirtualTagsMutation = useMutation({
        mutationFn: async () => {
            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("Authorization", "Bearer " + keycloak.token);

            const body =  JSON.stringify({
                VirtualTags: data.map(tag => tag.id),
                async_mode: true,
            });

            const requestOptions = {
                method: "POST",
                headers: headers,
                body: body,
            };

            const response = await fetch(API_URL + "/api/v1/allocation/process", requestOptions);
            
            if (!response.ok) {
                throw new Error("Failed to process virtual tags");
            }

            return response.json();
        },
        onSuccess: (data) => {
            // Extract job ID from response - try both possible field names
            const jobId = data.allocation_result.job_id;
            if (jobId) {
                console.log("Job ID received:", jobId);
                setCurrentJobId(jobId);
            } else {
                console.log("No job ID found in response");
                console.log("Available keys:", Object.keys(data));
            }
            
            // Show success notification
            setNotification({
                show: true,
                message: "Virtual tags processing started. Polling for status...",
                type: "success"
            });
        },
        onError: (error) => {
            console.error("Error processing virtual tags:", error);
            setNotification({
                show: true,
                message: "Failed to process virtual tags",
                type: "error"
            });
        }
    });

    const processVirtualTags = () => {
        // Reset previous job state when starting new processing
        setCurrentJobId(null);
        setShouldPoll(false);
        
        // Clear any previous job status data from the query cache
        queryClient.removeQueries({ queryKey: ["allocationJobStatus"] });
        
        // Show job status modal immediately
        setShowJobStatusModal(true);
        
        // Start polling immediately when process is initiated
        setShouldPoll(true);
        processVirtualTagsMutation.mutate();
    }

    // Function to get the allocation job status by job ID
    const getAllocationJobStatus = async (token, jobId) => {
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("Authorization", "Bearer " + token);

        const requestOptions = {
            method: "GET",
            headers: headers,
        };

        const response = await fetch(API_URL + `/api/v1/allocation/status/${jobId}`, requestOptions);
        
        if (!response.ok) {
            throw new Error("Failed to fetch allocation job status");
        }

        return response.json();
    }

    // Query for getting the allocation job status
    const { data: jobStatusData, refetch: refetchJobStatus, isLoading: isLoadingJobStatus } = useQuery({
        queryKey: ["allocationJobStatus", currentJobId],
        queryFn: () => {
            return getAllocationJobStatus(keycloak.token, currentJobId);
        },
        enabled: keycloak.token !== "" && shouldPoll && currentJobId !== null,
        refetchInterval: shouldPoll && currentJobId ? 5000 : false, // Poll every 5 seconds only when shouldPoll is true and we have a job ID
        refetchIntervalInBackground: shouldPoll && currentJobId !== null,
        retry: (failureCount, error) => {
            // Retry on network errors, but not on missing job ID
            return failureCount < 3 && !error.message.includes("No job ID available yet");
        },
    });

    const pollJobStatus = () => {
        if (currentJobId) {
            console.log("Starting polling for job:", currentJobId);
            setShouldPoll(true);
            refetchJobStatus();
        } else {
            console.log("No current job ID, showing warning");
            // If no current job ID, we can't check status
            setNotification({
                show: true,
                message: "No job in progress. Please start a new job first.",
                type: "warning"
            });
        }
    }

    // Trigger job status fetch when modal is opened and we have a job ID
    useEffect(() => {
        if (showJobStatusModal && currentJobId && shouldPoll) {
            console.log("Modal opened, triggering job status fetch for job:", currentJobId);
            refetchJobStatus();
        }
    }, [showJobStatusModal, currentJobId, shouldPoll, refetchJobStatus]);

    // Stop polling when job is completed or failed
    useEffect(() => {
        if (jobStatusData && shouldPoll) {
            console.log("jobStatusData", jobStatusData);
            console.log("jobStatusData.result", jobStatusData.result);
            console.log("jobStatusData keys:", Object.keys(jobStatusData));
            console.log("jobStatusData.status:", jobStatusData.status);
            console.log("jobStatusData.progress:", jobStatusData.progress);
            
            const status = jobStatusData.status;
            console.log("Checking status:", status, "shouldPoll:", shouldPoll);
            
            if (status === 'completed' || status === 'failed' || status === 'cancelled') {
                console.log("Job finished with status:", status);
                setShouldPoll(false);
                
                // Show completion notification
                if (status === 'completed') {
                    setNotification({
                        show: true,
                        message: `Allocation completed successfully! Processed ${jobStatusData.result?.processed_rows || 0} rows.`,
                        type: "success"
                    });
                } else if (status === 'failed') {
                    setNotification({
                        show: true,
                        message: "Allocation failed. Please check the job status for details.",
                        type: "error"
                    });
                }
            } else {
                console.log("Job still running with status:", status);
            }
        }
    }, [jobStatusData, shouldPoll]);

    // Auto-hide notifications after 5 seconds
    useEffect(() => {
        if (notification.show) {
            const timer = setTimeout(() => {
                setNotification({ show: false, message: "", type: "" });
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [notification.show]);



    const deleteVirtualTag = () => {
        if (virtualTag && virtualTag.id) {
            deleteVirtualTagMutation.mutate(virtualTag.id);
        }
    }

    return (
        <Fragment>
            <AdministrationHeader
				title="Virtual Tags"
                subTitle="Define and manage virtual tags that are used for cost allocation"
				className="text-text-3"
                icon={icon({name: "folder-tree", style: "light"})}
			/>
            <div className="text-left flex flex-auto flex-col ml-10 mt-6 mr-6">
                <TableCard
                    title="Virtual Tags"
                    subtitle={isLoadingVirtualTags ? "Loading…" : `Showing ${data.length} ${data.length === 1 ? "virtual tag" : "virtual tags"}`}
                    primaryAction={{ label: "Add New Virtual Tag", icon: icon({ name: "plus" }), onClick: createVirtualTag }}
                    controlsPlacement="first-row"
                    controls={
                        <button
                            type="button"
                            onClick={() => {
                                if (shouldPoll) {
                                    pollJobStatus();
                                    setShowJobStatusModal(true);
                                } else {
                                    processVirtualTags();
                                }
                            }}
                            disabled={isLoadingJobStatus || processVirtualTagsMutation.isPending}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-ctrl border border-card-bd bg-card px-3 py-1.5 text-[13px] font-medium text-text-2 transition-colors hover:bg-surface-2 hover:text-text disabled:opacity-50"
                        >
                            <FontAwesomeIcon
                                icon={shouldPoll ? icon({ name: "clock", style: "regular" }) : icon({ name: "rotate", style: "regular" })}
                                className="text-[13px]"
                            />
                            {shouldPoll ? "Check Job Status" : "Process Virtual Tags"}
                        </button>
                    }
                    isLoading={isLoadingVirtualTags}
                    error={virtualTagsError}
                    errorMessage="Error loading virtual tags"
                    onRetry={() => refetchVirtualTags()}
                >
                    {(!data || data.length === 0) ? (
                        <EmptyState
                            icon={icon({ name: "folder-tree", style: "light" })}
                            title="No virtual tags yet"
                            message="Create a virtual tag to allocate costs with custom rules."
                            action={{ label: "Add virtual tag", onClick: createVirtualTag, icon: icon({ name: "plus", style: "regular" }) }}
                        />
                    ) : (
                        <VirtualTagsTable
                            data={data}
                            setData={handleVirtualTagReorder}
                            selectVirtualTag={selectVirtualTag}
                        />
                    )}
                </TableCard>
            </div>

            <ModalVirtualTagEditor
                show={ruleEditorWindow}
                close={() => setRuleEditorWindow(false)}
                saveVirtualTag={saveVirtualTag}
                virtualTag={virtualTag}
                setVirtualTag={setVirtualTag}
                groupingValues={groupingValues}
                isLoading={createVirtualTagMutation.isPending || updateVirtualTagMutation.isPending}
                deleteVirtualTag={deleteVirtualTag}
                isDeleting={deleteVirtualTagMutation.isPending}
            />

            <JobStatusModal
                show={showJobStatusModal}
                close={() => setShowJobStatusModal(false)}
                jobStatusData={jobStatusData}
                currentJobId={currentJobId}
                shouldPoll={shouldPoll}
                isLoadingJobStatus={isLoadingJobStatus}
            />
        </Fragment>
    );
};


export default VirtualTags;

